import type { FileRejections } from "@v2/features/products/utils";

import * as React from "react";

import {
  SvgDimensionError,
  useUploadAsset,
} from "@v2/features/assets/useUploadAsset";
import { analytics, trackError } from "@v2/features/infra/analytics/tracking";
import { handleFileRejections } from "@v2/features/products/utils";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useErrorToast } from "@v2/hooks/useErrorToast";
import { useDropzone } from "react-dropzone";
import { MAX_FILE_UPLOAD_COUNT } from "schemas/asset";
import { v4 as uuidv4 } from "uuid";

import { sizeValidator } from "./utils/sizeValidator";

type CommonProps = {
  onDropBeforeUpload?: (file: File) => string | null;
  onUploadComplete: ({
    url,
    imageComponentId,
    file,
    id,
  }: {
    url: string;
    imageComponentId: string | null;
    file?: File;
    id?: string;
  }) => void;
  onError?: (imageComponentId: string | null) => void;
  acceptDropAssetType: string[] | null | undefined;
  allowClickToUpload: boolean;
  isDisabled?: boolean;
};

type FileDropZoneResult = {
  getRootProps: ReturnType<typeof useDropzone>["getRootProps"];
  getInputProps: ReturnType<typeof useDropzone>["getInputProps"];
  openFileDialog: () => void;
  isDragActive: boolean;
  droppedFilesMap: Map<string, File>;
  acceptedFiles: File[];
  uploadFiles: (files: File[]) => void;
  isUploading: boolean;
};

export function useReploFileDropZone(props: CommonProps): FileDropZoneResult {
  const { uploadAsset, isUploading } = useUploadAsset();
  const result = useFileDropZone({
    ...props,
    uploadFile: async ({ id, file }) => {
      const url = await uploadAsset({ assetId: id, file });
      return url;
    },
  });
  return { ...result, isUploading };
}

function useFileDropZone({
  onDropBeforeUpload,
  onUploadComplete,
  onError,
  acceptDropAssetType,
  allowClickToUpload,
  isDisabled = false,
  uploadFile,
}: CommonProps & {
  uploadFile: (args: { file: File; id: string }) => Promise<string>;
}) {
  const [droppedFilesMap, setDroppedFilesMap] = React.useState<
    Map<string, File>
  >(new Map());
  const projectId = useCurrentProjectId();

  const errorToast = useErrorToast();

  const handleError = React.useCallback(
    ({
      id,
      imageComponentId,
    }: {
      id: string;
      imageComponentId?: string | null;
    }) => {
      setDroppedFilesMap((prev) => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
      onError?.(imageComponentId ?? null);
    },
    [onError],
  );

  const uploadFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      const internalDroppedFilesMap = new Map(
        files.map((file) => [uuidv4(), file]),
      );
      setDroppedFilesMap(internalDroppedFilesMap);

      const uploadPromises = Array.from(internalDroppedFilesMap.entries()).map(
        async ([id, file]) => {
          const imageComponentId = onDropBeforeUpload?.(file);
          try {
            const url = await uploadFile({ file, id });
            onUploadComplete({
              url,
              imageComponentId: imageComponentId ?? null,
              file,
              id,
            });
            analytics.logEvent("asset.upload.success", {
              projectId: projectId!,
              assetId: id,
              contentType: file.type,
              publicUrl: url,
            });
          } catch (error) {
            const extras = {
              projectId: projectId!,
              fileName: file.name,
              contentType: file.type,
              size: file.size,
            };
            trackError(error, extras);
            analytics.logEvent("asset.upload.failed", {
              error,
              ...extras,
            });
            // NOTE (Nick, 2026-01-15, REPL-22220): Show specific error for SVGs without dimensions
            if (error instanceof SvgDimensionError) {
              errorToast(
                "Invalid SVG",
                "This SVG is missing dimensions. Please try again with a different image!",
                "error.asset.upload.svg_dimensions",
                {},
              );
            } else {
              errorToast(
                "Error Uploading Asset",
                "An error occurred while uploading the asset. Please try again.",
                "error.asset.upload.unknown",
                {},
              );
            }
            handleError({ id, imageComponentId });
          }
        },
      );

      // NOTE (Sebas, 2025-01-31): We cannot await the uploadPromises because the onDrop
      // function is called synchronously and the type does not allow for async.
      void Promise.all(uploadPromises);
    },
    [
      onDropBeforeUpload,
      onUploadComplete,
      uploadFile,
      projectId,
      errorToast,
      handleError,
    ],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
    acceptedFiles,
  } = useDropzone({
    onDrop: (acceptedFiles: File[], fileRejections: FileRejections) => {
      const shouldStopProcessing = handleFileRejections(
        fileRejections,
        errorToast,
      );
      if (shouldStopProcessing) {
        return;
      }
      uploadFiles(acceptedFiles);
    },
    accept: acceptDropAssetType ?? undefined,
    maxFiles: MAX_FILE_UPLOAD_COUNT,
    noClick: !allowClickToUpload,
    noKeyboard: true,
    validator: sizeValidator,
    // NOTE (Kevin, 2025-12-15): Use noDrag instead of disabled so openFileDialog still works
    // to allow click-to-upload while letting global interceptor handle drag/drop
    noDrag: isDisabled,
  });

  return {
    getRootProps,
    getInputProps,
    openFileDialog,
    isDragActive,
    droppedFilesMap,
    acceptedFiles,
    uploadFiles,
  };
}
